import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from './data';
import { ChevronRight, ArrowRight, Copy, Check, Share2, Link as LinkIcon, Home, Twitter, Facebook, Linkedin, Mail } from 'lucide-react';

const ReferralPage = () => {
  const [name, setName] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [isNameValid, setIsNameValid] = useState(true);
  
  // Get current year for copyright
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Get base URL without any query parameters
    const url = window.location.href.split('?')[0];
    // Remove '/referral' from the end to get the base site URL
    const baseUrlWithoutReferral = url.endsWith('/referral') 
      ? url.substring(0, url.length - 9) 
      : url;
    setBaseUrl(baseUrlWithoutReferral);
  }, []);

  const handleNameChange = (e) => {
    setName(e.target.value);
    setIsNameValid(true);
  };

  const generateReferralLink = () => {
    if (!name.trim()) {
      setIsNameValid(false);
      return;
    }

    // Create UTM parameters
    const utmParams = new URLSearchParams();
    utmParams.append('utm_source', CONSTANTS.REFERRAL.UTM_PARAMS.SOURCE);
    utmParams.append('utm_medium', CONSTANTS.REFERRAL.UTM_PARAMS.MEDIUM);
    utmParams.append('utm_campaign', CONSTANTS.REFERRAL.UTM_PARAMS.CAMPAIGN);
    utmParams.append('utm_content', name.trim());

    // Generate the full referral link
    const fullReferralLink = `${baseUrl}?${utmParams.toString()}`;
    setReferralLink(fullReferralLink);
    setLinkGenerated(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Sharing functions
  const shareOnTwitter = () => {
    const text = `Join me on Major League Prompting - the premier platform for prompt engineers! ${referralLink}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const title = "Major League Prompting - Prompt Engineering Competitions";
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}&title=${encodeURIComponent(title)}`, '_blank');
  };

  const shareByEmail = () => {
    const subject = "Join me on Major League Prompting";
    const body = `Hey! I thought you might be interested in checking out Major League Prompting - a platform for competitive prompt engineering. Join using my referral link: ${referralLink}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-gray-800 min-h-screen text-white relative overflow-hidden">
      {/* Background Patterns */}
      <div className="absolute top-0 left-0 right-0 h-screen overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-pink-600 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 left-1/4 w-80 h-80 bg-blue-600 rounded-full filter blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="container mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center">
            <motion.div
              className="ml-3 font-bold text-xl"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="text-white">{CONSTANTS.SITE_NAME.split(' ')[0]} </span>
              <span className="text-white">{CONSTANTS.SITE_NAME.split(' ')[1]} </span>
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">{CONSTANTS.SITE_NAME.split(' ')[2]}</span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <a 
              href="/"
              className="px-5 py-2 rounded-full bg-gray-800 border border-gray-700 text-white font-medium hover:bg-gray-700 transition-all duration-300 flex items-center"
            >
              <Home className="mr-2 w-4 h-4" /> Back to Home
            </a>
          </motion.div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
            {CONSTANTS.REFERRAL.TAG}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {CONSTANTS.REFERRAL.TITLE}
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl">
            {CONSTANTS.REFERRAL.SUBTITLE}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left column */}
            <div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50 mb-8">
                <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                  Create Your Referral Link
                </h2>
                
                <div className="mb-6">
                  <label htmlFor="name" className="block text-gray-300 mb-2 text-sm font-medium">
                    {CONSTANTS.REFERRAL.FORM.NAME_LABEL}
                  </label>
                  <input
                    type="text"
                    id="name"
                    className={`w-full px-4 py-3 bg-gray-800 border ${isNameValid ? 'border-gray-600 focus:border-purple-500' : 'border-red-500'} rounded-lg focus:outline-none text-white shadow-inner`}
                    placeholder={CONSTANTS.REFERRAL.FORM.NAME_PLACEHOLDER}
                    value={name}
                    onChange={handleNameChange}
                  />
                  {!isNameValid && (
                    <p className="text-red-500 text-sm mt-1">Please enter your name</p>
                  )}
                  <p className="text-gray-400 text-xs mt-2">
                    {CONSTANTS.REFERRAL.FORM.NAME_HELP_TEXT}
                  </p>
                </div>
                
                <motion.button
                  onClick={generateReferralLink}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 flex items-center justify-center shadow-lg shadow-purple-500/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {CONSTANTS.REFERRAL.FORM.BUTTON_TEXT}
                  <LinkIcon className="ml-2 w-5 h-5" />
                </motion.button>
              </div>
              
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-xl font-bold mb-4">
                  {CONSTANTS.REFERRAL.HOW_IT_WORKS.TITLE}
                </h3>
                <ul className="space-y-4">
                  {CONSTANTS.REFERRAL.HOW_IT_WORKS.STEPS.map((step, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-900/50 rounded-full flex items-center justify-center mt-0.5 mr-3 text-xs font-bold">
                        {index + 1}
                      </div>
                      <span className="text-gray-300">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Right column */}
            <div>
              {linkGenerated ? (
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50 mb-8">
                  <h3 className="text-xl font-bold mb-4">
                    {CONSTANTS.REFERRAL.LINK_SECTION.TITLE}
                  </h3>
                  <div className="bg-gray-900 rounded-lg p-3 mb-4 flex items-center border border-gray-700">
                    <div className="overflow-x-auto text-gray-300 text-sm flex-1">
                      {referralLink}
                    </div>
                    <motion.button
                      onClick={copyToClipboard}
                      className={`ml-2 p-2 rounded-full ${copied ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </motion.button>
                  </div>
                  
                  <div>
                    <h4 className="text-sm text-gray-400 mb-3">
                      {CONSTANTS.REFERRAL.LINK_SECTION.SOCIAL_SHARE_TITLE}
                    </h4>
                    <div className="flex space-x-3">
                      <motion.button
                        onClick={shareOnTwitter}
                        className="p-2 bg-[#1DA1F2]/10 text-[#1DA1F2] rounded-full hover:bg-[#1DA1F2]/20 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Twitter className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        onClick={shareOnFacebook}
                        className="p-2 bg-[#4267B2]/10 text-[#4267B2] rounded-full hover:bg-[#4267B2]/20 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Facebook className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        onClick={shareOnLinkedIn}
                        className="p-2 bg-[#0077B5]/10 text-[#0077B5] rounded-full hover:bg-[#0077B5]/20 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Linkedin className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        onClick={shareByEmail}
                        className="p-2 bg-gray-700/30 text-gray-300 rounded-full hover:bg-gray-700/50 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Mail className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl p-6 border border-gray-700/30 mb-8 flex items-center justify-center min-h-[200px]">
                  <div className="text-center text-gray-500">
                    <Share2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Enter your name and generate a referral link to share</p>
                  </div>
                </div>
              )}
              
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
                  {CONSTANTS.REFERRAL.BENEFITS.TITLE}
                </h3>
                <ul className="space-y-3">
                  {CONSTANTS.REFERRAL.BENEFITS.ITEMS.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 bg-purple-900/50 rounded-full flex items-center justify-center mt-1 mr-2">
                        <Check className="w-3 h-3 text-purple-400" />
                      </div>
                      <span className="text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-900 py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © {currentYear} Major League Prompting. All rights reserved.
            </p>
            <div className="flex space-x-6">
              {CONSTANTS.FOOTER.LEGAL.map((link, index) => (
                <a 
                  key={index} 
                  href={link.url} 
                  className="text-gray-500 hover:text-white transition-colors duration-300 text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReferralPage;
